// src/resources/dashboards/ProductEvolution/ChainDetailView.tsx
//
// Painel "Evolução do Produto" — DETALHE de uma rede (tela 2 de 2; a carteira
// vive em /dashboard-evolution).
//
// Uma request por seção, como no Painel 360. O detalhe não usa rollup: com
// chain_id em todo WHERE cada seção fica sub-segundo ao vivo, e a tela deixa de
// servir número velho justamente para a rede que o operador está olhando.
//
// Três regras herdadas de defeitos reais da tela antiga:
//   1. Isolamento. Uma seção que erra vira ErrorCard com retry; as outras seis
//      renderizam. Não existe "a tela inteira quebrou".
//   2. Stale-while-revalidate. Trocar o período NÃO explode as sete seções em
//      skeleton — o conteúdo anterior fica no lugar até a resposta chegar.
//   3. Nada de visual "futurista". Sem keyframes de glow infinito: cardSx e
//      COLORS do kit compartilhado, iguais aos do Painel 360.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import { usePermissions } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '../../../constants';
import { EVENTS, track } from '../../../lib/analytics';
import { COLORS, Section, useDashboardSection } from '../shared';
import {
  ChainBillingData, ChainCustomersData, ChainDetailFilter, ChainModulesData,
  ChainOverviewData, ChainPenetrationData, ChainSignalsData, ChainTicketData,
} from './types';
import OverviewSection from './sections/OverviewSection';
import TicketSection from './sections/TicketSection';
import PenetrationSection from './sections/PenetrationSection';
import CustomersSection from './sections/CustomersSection';
import ModulesSection from './sections/ModulesSection';
import BillingSection from './sections/BillingSection';
import SignalsSection from './sections/SignalsSection';

type Preset = '7d' | '30d' | '90d' | 'custom';

const PRESETS: { key: Preset; label: string; days?: number }[] = [
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
  { key: 'custom', label: 'Personalizado' },
];

const DEFAULT_PRESET: Preset = '30d';
const DEFAULT_DAYS = 30;

const ChainDetailView = () => {
  const { chainId } = useParams<{ chainId: string }>();
  const navigate = useNavigate();
  const { permissions, isLoading: permissionsLoading } = usePermissions();

  const [preset, setPreset] = useState<Preset>(DEFAULT_PRESET);
  const [from, setFrom] = useState(dayjs().subtract(DEFAULT_DAYS, 'day').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));

  useEffect(() => { track(EVENTS.DASHBOARD_OPENED, { dashboard: 'product-evolution-chain' }); }, []);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    const def = PRESETS.find(x => x.key === p);
    if (def?.days !== undefined) {
      setFrom(dayjs().subtract(def.days, 'day').format('YYYY-MM-DD'));
      setTo(dayjs().format('YYYY-MM-DD'));
    }
  };

  // Filtro ÚNICO e memoizado, propagado às 7 seções. `to` sai EXCLUSIVO: o
  // backend usa intervalo meia-aberto [from, to), e sem o +1 dia o dia final
  // escolhido pelo operador ficaria de fora.
  const filter: ChainDetailFilter = useMemo(() => ({
    from,
    to: dayjs(to).add(1, 'day').format('YYYY-MM-DD'),
  }), [from, to]);

  // `chainId` viaja no TOPO da querystring, não dentro do filtro: é assim que o
  // chainDetailValidator do backend espera. Sem ele, o store/providers/getAll.js
  // mandaria a rede do localStorage — que não é necessariamente a da URL.
  const extraParams = useMemo(() => ({ chainId }), [chainId]);
  const enabled = !!chainId;

  const overview = useDashboardSection<ChainOverviewData>('admin/metrics/chain-overview', filter, enabled, undefined, extraParams);
  const ticket = useDashboardSection<ChainTicketData>('admin/metrics/chain-ticket', filter, enabled, undefined, extraParams);
  const penetration = useDashboardSection<ChainPenetrationData>('admin/metrics/chain-penetration', filter, enabled, undefined, extraParams);
  const customers = useDashboardSection<ChainCustomersData>('admin/metrics/chain-customers', filter, enabled, undefined, extraParams);
  const modules = useDashboardSection<ChainModulesData>('admin/metrics/chain-modules', filter, enabled, undefined, extraParams);
  const billing = useDashboardSection<ChainBillingData>('admin/metrics/chain-billing', filter, enabled, undefined, extraParams);
  const signals = useDashboardSection<ChainSignalsData>('admin/metrics/chain-signals', filter, enabled, undefined, extraParams);

  const chain = overview.data?.chain ?? null;
  // fillin_summary é D-1. Se o último dia agregado é anterior a ontem, o Lambda
  // agregador atrasou e os números de volume estão velhos — melhor dizer.
  const dataThrough = overview.data?.dataThrough ?? null;
  const isStale = !!dataThrough && dayjs(dataThrough).isBefore(dayjs().subtract(1, 'day'), 'day');

  if (permissionsLoading) return null;

  // Mesmo gate do backend: isAdmin E product-evolution (o `+` do escopo Hapi é AND).
  const perms: string[] = permissions ?? [];
  if (!perms.includes(PERMISSIONS.ADMIN) || !perms.includes(PERMISSIONS.PRODUCT_EVOLUTION)) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">Acesso não autorizado.</Typography>
      </Box>
    );
  }

  if (!chainId) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: COLORS.ink }}>Rede não informada</Typography>
        <Button size="small" startIcon={<ArrowBackIcon />} sx={{ mt: 1.5, textTransform: 'none' }}
          onClick={() => navigate('/dashboard-evolution')}>
          Voltar para a carteira
        </Button>
      </Box>
    );
  }

  const periodLabel = `${dayjs(from).format('DD/MM/YYYY')} a ${dayjs(to).format('DD/MM/YYYY')}`;

  return (
    <Box sx={{ minHeight: '100vh', mx: -1, pb: 8, background: 'linear-gradient(145deg, #f0f4ff 0%, #f8fafc 45%, #f5f0ff 100%)' }}>
      {/* Header sticky full-width; conteúdo num wrap centralizado (maxWidth 1240). */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 30, mb: 1,
        backdropFilter: 'blur(14px)', background: 'rgba(248,250,252,0.85)',
        borderBottom: '1px solid rgba(120,105,223,0.10)',
      }}>
        <Box sx={{
          maxWidth: 1240, mx: 'auto', px: { xs: 2.5, md: 3 }, py: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5,
        }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => navigate('/dashboard-evolution')}
            sx={{
              textTransform: 'none', fontWeight: 700, color: COLORS.brand, px: 0.5,
              '&:hover': { bgcolor: 'rgba(120,105,223,0.06)' },
            }}
          >
            Voltar para a carteira
          </Button>

          <Box>
            <Typography sx={{
              fontSize: 26, fontWeight: 800,
              background: `linear-gradient(90deg, ${COLORS.brand2}, ${COLORS.brand}, #275030)`,
              WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {chain?.name ?? 'Detalhe da rede'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
              Evolução do Produto · {periodLabel} · comparado com o período anterior de mesmo tamanho
            </Typography>
            {!chain && overview.loading && (
              <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 0.25 }}>Carregando a rede…</Typography>
            )}
            {!chain && overview.error && (
              <Typography sx={{ fontSize: 11.5, color: COLORS.danger, mt: 0.25 }}>
                Não foi possível carregar o cabeçalho da rede — as demais seções continuam válidas.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {PRESETS.map(p => (
              <Chip
                key={p.key}
                label={p.label}
                onClick={() => applyPreset(p.key)}
                sx={preset === p.key ? {
                  fontWeight: 700, color: '#fff',
                  background: `linear-gradient(135deg, ${COLORS.brand2}, ${COLORS.brand})`,
                } : { fontWeight: 600, bgcolor: '#fff' }}
              />
            ))}
            {preset === 'custom' && (
              <>
                <TextField
                  type="date" size="small" value={from}
                  onChange={e => setFrom(e.target.value)}
                  inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                  sx={{ width: 150, bgcolor: '#fff' }}
                />
                <TextField
                  type="date" size="small" value={to}
                  onChange={e => setTo(e.target.value)}
                  inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                  sx={{ width: 150, bgcolor: '#fff' }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2.5, md: 3 } }}>
        {isStale && (
          <Box sx={{
            mt: 1.5, px: 2, py: 1.25, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)',
          }}>
            <WarningAmberIcon sx={{ fontSize: 18, color: COLORS.warning }} />
            <Typography sx={{ fontSize: 12.5, color: COLORS.ink }}>
              Os dados de volume vão até <b>{dayjs(dataThrough).format('DD/MM/YYYY')}</b> — a agregação diária está
              atrasada. Ticket, abastecimentos e receita podem estar defasados.
            </Typography>
          </Box>
        )}

        <Section index={0}><OverviewSection state={overview} /></Section>
        <Section index={1}><TicketSection state={ticket} /></Section>
        <Section index={2}><PenetrationSection state={penetration} /></Section>
        <Section index={3}><CustomersSection state={customers} /></Section>
        <Section index={4}><ModulesSection state={modules} /></Section>
        <Section index={5}><BillingSection state={billing} /></Section>
        <Section index={6}><SignalsSection state={signals} /></Section>
      </Box>
    </Box>
  );
};

export default ChainDetailView;
